import React from 'react';
import {
	IonContent,
	IonItem,
	IonItemGroup,
	IonLabel,
	IonPage,
	useIonAlert,
	useIonRouter,
	useIonToast,
} from '@ionic/react';
import { RouteComponentProps } from 'react-router';
import moment from 'moment';
import { Modal } from 'antd';
import { axius, func, types } from 'utils';
import { Header, TabsOffset, Verify } from 'components';
import { _store, _update } from '_Store';
import { Alert } from 'react-bootstrap';

import './_User.scss';

// user forms
const Login = React.lazy(() => import('../Auth/Login'));
const FormEmail = React.lazy(() => import('./FormEmail'));
const FormPhone = React.lazy(() => import('./FormPhone'));
const FormProfile = React.lazy(() => import('./FormProfile'));

interface Props extends RouteComponentProps, types.StateProps {}

const User: React.FC<Props> = (props) => {
	const [presentAlert] = useIonAlert();
	const [presentToast] = useIonToast();
	const router = useIonRouter();

	const {
		__auth: { user, isLoggedIn },
		__data: {
			cartDefault,
			settings: { countries },
			activeStore,
			location,
		},
	} = _store.useState();

	const [edit, setEdit] = React.useState('');

	React.useEffect(() => {
		if (func.getStorage('defaultPWD')) {
			setEdit(`password`);
		}
	}, []);

	const kyc = func.checkKYC(user);

	const lists = [
		// { key: 'orders', label: `<div>Orders</div>` },
		{ key: 'favorites', label: `<div>Favorites</div>` },
		{ key: 'wallet', label: `<div>Wallet</div>` },
		{ key: 'break' },
		{
			key: 'profile',
			label: `
                        <div>Profile Details
							<div class="text-muted mt-2 ${kyc.phone ? 'd-flex' : ''} justify-content-between align-items-center">						
								<div class="small">${user.name} • ${func.ucWords(user.gender || 'Gender')} • ${user.birthDate ? moment(user.birthDate).format('LL') : 'Birth date'}</div>
								<div><i class="fa fa-user-edit"></i></div>
							</div>
                        </div>
                    `,
		},
		{
			key: 'phone',
			label: `
				<div>
					Phone Number
					<div class="text-muted mt-2 ${kyc.phone ? 'd-flex' : ''} justify-content-between align-items-center">						
						<div class="small">${func.format.phoneNumber(user?.phoneNumber)}</div>
						<div>
							${kyc.phone ? '<i class="fa fa-check-circle primary"></i> <i class="ml-1 fa fa-pencil-alt"></i>' : ''}
						</div>
					</div>
					${!kyc.phone ? '<div class="alert alert-warning mt-2 small p-2 mb-0"><i class="mr-1 fa fa-exclamation-circle"></i> Verify your phone number</div>' : ''}
				</div>
			`,
		},
		{
			key: 'email',
			label: `
				<div>
					Email Address
					<div class="text-muted mt-2 ${kyc.email ? 'd-flex' : ''} justify-content-between align-items-center">						
						<div class="small">${user?.email || 'N/A'}</div>
						<div>${kyc.email ? '<i class="fa fa-check-circle primary"></i> <i class="ml-1 fa fa-pencil-alt"></i>' : ''}</div>
					</div>
					${!kyc.email ? '<div class="alert alert-warning mt-2 small p-2 mb-0"><i class="mr-1 fa fa-exclamation-circle"></i> Verify your email address</div>' : ''}
				</div>
			`,
		},
		{ key: 'break' },
		{ key: 'logout', label: `<div>Log Out</div>` },
	];

	const gotoPage = (key: string) => {
		switch (key) {
			default:
				setEdit(
					`${['email', 'phone'].includes(key) && !kyc[key as 'email' | 'phone'] && user[key as keyof types.User] ? 'verify-' : ''}${key}`
				);
				break;

			case 'favorites':
			case 'orders':
			case 'wallet':
				router.push('/user/' + key);
				break;

			case 'logout':
				presentAlert({
					header: 'Oops!',
					message: 'Are you sure you want to logout?',
					buttons: [
						{ text: 'Cancel' },
						{
							text: 'Yes, Log out',
							handler: async () => {
								axius.get('auth/logout').then(() => {
									_update.data('item', {});
									_update.data('cart', cartDefault);
									_update.data('order', {});
									_update.data('activeStore', {});
									_update.data('deliveryFees', []);

									_update.auth('user', {});
									_update.auth('token', '');
									func.init();
									func.redirect('/');
								});
							},
						},
					],
				});
				break;
		}
	};

	return (
		<React.Fragment>
			{isLoggedIn ? (
				<IonPage id="User">
					<Header {...props} />

					<IonContent fullscreen={false}>
						<div className="p-3 text-center text-white fw-600 m-4 title">
							<span className="secondary">Hello,</span>{' '}
							<span className="">{user?.firstName}</span>
						</div>

						<div className="px-3">
							<div>&nbsp;</div>
							<IonItemGroup className="mt-0">
								{lists.map((item: any, i: number) =>
									item.key === 'break' ? (
										<div
											key={`${i}-${item.key}`}
											id={`${i}-${item.key}`}
											className="divider my-3"
										/>
									) : (
										<div key={`${i}-${item.key}`} id={`${i}-${item.key}`}>
											<IonItem
												button
												lines="none"
												mode="md"
												className="animate__animated animate__fadeIn"
												onClick={() => gotoPage(item.key)}
											>
												<IonLabel>
													<div
														className="my-1"
														dangerouslySetInnerHTML={{ __html: item.label }}
													/>
													{((item.key === 'email' && !kyc.email) ||
														(item.key === 'phone' && !kyc.phone)) && (
														<>
															<Alert
																variant="warning"
																className="small text-xs mb-0"
															>
																<i className="fa fa-exclamation-circle primary" />{' '}
																{item.key === 'email'
																	? 'Verify Email address'
																	: 'Verify Phone number'}{' '}
															</Alert>
														</>
													)}
												</IonLabel>
											</IonItem>
										</div>
									)
								)}
							</IonItemGroup>

							<p>&nbsp;</p>
							{countries?.length > 0 && (
								<div className="text-center text-muted small">
									{func.venueName(activeStore)} <br />
									{func.distanceMatrix(location.address, activeStore.address)}
								</div>
							)}
							<div
								className="text-center muted small mt-2"
								onClick={() => func.clearCache()}
							>
								Republic Dash v{func.app.version}
							</div>
						</div>

						<Modal
							open={edit ? true : false}
							width="90vw"
							centered
							title={null}
							footer={null}
							destroyOnHidden={true}
							maskClosable={false}
							onCancel={() => setEdit('')}
						>
							{edit === 'profile' && (
								<React.Suspense>
									<FormProfile {...props} onSuccess={() => setEdit('')} />
								</React.Suspense>
							)}
							{edit === 'phone' && (
								<React.Suspense>
									<FormPhone {...props} onSuccess={() => setEdit('')} />
								</React.Suspense>
							)}
							{edit === 'email' && (
								<React.Suspense>
									<FormEmail {...props} onSuccess={() => setEdit('')} />
								</React.Suspense>
							)}
							{edit === 'verify-phone' && (
								<Verify
									title={'Verify your phone number'}
									sendOtp={true}
									channel={types.CodeChannels.SMS}
									username={user.phoneNumber}
									onSuccess={() => {
										setEdit('');
										presentToast({
											message: 'Phone number verified successfully!',
											duration: 1500,
											position: 'top',
										});
										_update.data('cache', {
											user: new Date().getTime(),
										});
									}}
								/>
							)}
							{edit === 'verify-email' && (
								<Verify
									title={'Verify your email address'}
									sendOtp={true}
									channel={types.CodeChannels.EMAIL}
									username={user.email}
									onSuccess={() => {
										const sentToVerify = func.getStorageJson('edit_user') || {};
										setEdit('');
										presentToast({
											message: `Email address verified successfully!${sentToVerify.from ? ' Redirecting to checkout...' : ''}`,
											duration: 1500,
											position: 'top',
										});
										_update.data('cache', {
											user: new Date().getTime(),
										});
										if (sentToVerify.from) {
											router.push(sentToVerify.from);
										}
									}}
								/>
							)}
						</Modal>
						<TabsOffset />
					</IonContent>
				</IonPage>
			) : (
				<React.Suspense>
					<Login {...props} />
				</React.Suspense>
			)}
		</React.Fragment>
	);
};

export default User;
